import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../application/services/ApiContextService';
import LocalizationService from '../../application/services/LocalizationService';
import apiProxyService from '../../infrastructure/api/ApiProxyService';
import {
  ArrowLeft,
  KeyRound,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import './ResetPasswordPage.css';

type ResetPasswordParams = {
  token: string;
};

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<keyof ResetPasswordParams>();
  const navigate = useNavigate();
  const { executeRequest, isLoading, error } = useApi();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenExists, setTokenExists] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Çeviriler için dile bağlı metinler
  const t = (key: string) => LocalizationService.translate(`resetPassword.${key}`) as string;

  // Form durumları
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [passwordMatchOk, setPasswordMatchOk] = useState(false);

  // Requirement checks
  const reqLen     = password.length >= 8;
  const reqUpper   = /[A-Z]/.test(password);
  const reqLower   = /[a-z]/.test(password);
  const reqNum     = /\d/.test(password);
  const reqSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

  useEffect(() => {
    const urlToken = token || new URLSearchParams(window.location.search).get('token');
    const urlEmail = new URLSearchParams(window.location.search).get('email');

    if (urlEmail) setEmail(urlEmail);
    if (!urlToken) {
      setTokenExists(false);
      setFormError(LocalizationService.translate('resetPassword.notFound', 'errors') as string);
    }
  }, [token]);

  // Şifre güçlendirme kontrolü
  useEffect(() => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }
    const hasUpperCase  = /[A-Z]/.test(password);
    const hasLowerCase  = /[a-z]/.test(password);
    const hasNumbers    = /\d/.test(password);
    const hasSpecial    = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password);
    const isLongEnough  = password.length >= 8;
    const score = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecial, isLongEnough].filter(Boolean).length;

    if (score < 3)      setPasswordStrength('weak');
    else if (score < 5) setPasswordStrength('medium');
    else                setPasswordStrength('strong');
  }, [password]);

  // Şifre eşleşme kontrolü
  useEffect(() => {
    if (!confirmPassword) {
      setPasswordMatchError(false);
      setPasswordMatchOk(false);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(true);
      setPasswordMatchOk(false);
    } else {
      setPasswordMatchError(false);
      setPasswordMatchOk(true);
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSuccess(false);

    if (!password || !confirmPassword) {
      setFormError(t('passwordRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t('passwordsDoNotMatchError'));
      return;
    }
    if (passwordStrength === 'weak') {
      setFormError(t('passwordTooWeak'));
      return;
    }

    try {
      const urlToken = token || new URLSearchParams(window.location.search).get('token');
      if (!urlToken) {
        setTokenExists(false);
        setFormError(LocalizationService.translate('resetPassword.notFound', 'errors') as string);
        return;
      }

      const response = await executeRequest(() =>
        apiProxyService.resetPassword(urlToken, password, email)
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => { navigate('/login'); }, 3000);
      } else {
        if (response.statusCode === 404) {
          setTokenExists(false);
          setFormError(LocalizationService.translate('resetPassword.notFound', 'errors') as string);
          setTimeout(() => { navigate('/forgot-password'); }, 5000);
          return;
        }

        const errorKey = response.error;
        if (errorKey && errorKey.startsWith('errors:')) {
          setFormError(LocalizationService.translate(errorKey.substring(7), 'errors') as string);
        } else {
          setFormError(errorKey || 'Şifre yenileme işlemi başarısız oldu.');
        }

        if (response.errorCode === 'PASSWORD_POLICY_VIOLATION') {
          setPasswordStrength('weak');
        }
        if (response.errorCode === 'INVALID_TOKEN' || response.statusCode === 410) {
          setTokenExists(false);
          setTimeout(() => { navigate('/forgot-password'); }, 5000);
        }
      }
    } catch (err) {
      console.error('Şifre yenileme hatası:', err);
      setFormError('Şifre yenileme işlemi sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    }
  };

  const logoArea = (
    <header className="rp-header">
      <button className="rp-logo-link" onClick={() => navigate('/')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <div className="rp-logo-mark">
          <div className="rp-logo-inner">
            <span className="rp-logo-g">G</span>
          </div>
        </div>
        <span className="rp-logo-txt">GetSpeakHub</span>
      </button>
    </header>
  );

  const footer = (
    <footer className="rp-footer">
      <a href="/terms">Terms of Service</a>
      {' · '}
      <a href="/privacy">Privacy Policy</a>
      {' · '}
      © 2026 GetSpeakHub, Inc.
    </footer>
  );

  // ── SUCCESS STATE ──
  if (success) {
    return (
      <div className="rp-page">
        {logoArea}
        <main className="rp-main">
          <div className="rp-card">
            <div className="rp-state">
              <div className="rp-state-icon-wrap rp-state-icon-success">
                <CheckCircle2 size={44} color="#10B981" strokeWidth={2} />
              </div>
              <h2 className="rp-state-title">{t('successTitle') || 'Password updated.'}</h2>
              <p className="rp-state-desc">
                {t('successDescription') || "Your password has been reset successfully. You'll be redirected to the sign-in page in a moment."}
              </p>
              <button className="rp-btn-submit" onClick={() => navigate('/login')}>
                Go to Sign In
              </button>
            </div>
          </div>
        </main>
        {footer}
      </div>
    );
  }

  // ── INVALID TOKEN STATE ──
  if (!tokenExists) {
    return (
      <div className="rp-page">
        {logoArea}
        <main className="rp-main">
          <div className="rp-card">
            <div className="rp-state">
              <div className="rp-state-icon-wrap rp-state-icon-error">
                <XCircle size={44} color="#EF4444" strokeWidth={2} />
              </div>
              <h2 className="rp-state-title">{t('invalidTokenTitle') || 'Link expired.'}</h2>
              <p className="rp-state-desc">
                {(LocalizationService.translate('resetPassword.notFound', 'errors') as string) ||
                  'This password reset link is invalid or has expired. Request a new one from the sign-in page.'}
              </p>
              <button className="rp-btn-submit" onClick={() => navigate('/forgot-password')}>
                {t('requestNewLink') || 'Request New Link'}
              </button>
              <button className="rp-btn-ghost" onClick={() => navigate('/login')}>
                <ArrowLeft size={16} />
                Back to Sign In
              </button>
            </div>
          </div>
        </main>
        {footer}
      </div>
    );
  }

  // ── FORM STATE ──
  const metCount = [reqLen, reqUpper, reqLower, reqNum, reqSpecial].filter(Boolean).length;

  const strengthLabel =
    passwordStrength === 'weak'   ? (t('strengthWeak') || 'Weak') :
    passwordStrength === 'medium' ? (t('strengthMedium') || 'Medium') :
    passwordStrength === 'strong' ? (t('strengthStrong') || 'Strong') : '';

  return (
    <div className="rp-page">
      {logoArea}

      <main className="rp-main">
        <div className="rp-card">

          {/* Back button */}
          <button className="rp-back-btn" onClick={() => navigate('/login')}>
            <ArrowLeft size={15} />
            Back to sign in
          </button>

          {/* Card icon */}
          <div className="rp-card-icon">
            <KeyRound size={30} />
          </div>

          <h1 className="rp-card-title">{t('title') || 'Reset your password'}</h1>
          <p className="rp-card-desc">
            {t('description') || 'Choose a strong new password. Once you save, you\'ll be signed in automatically.'}
          </p>

          {/* Error banner */}
          {(formError || error) && (
            <div className="rp-error-banner">
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{formError || error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* New Password */}
            <div className="rp-field">
              <label className="rp-field-label">{t('passwordLabel') || 'New Password'}</label>
              <div className="rp-field-wrap">
                <span className="rp-field-icon"><Lock size={18} /></span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="rp-field-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder') || 'Create a strong password'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="rp-field-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Strength bars */}
              {password && (
                <div className="rp-strength-wrap">
                  <div className="rp-strength-bars">
                    <div className={`rp-strength-bar ${metCount >= 1 ? passwordStrength ?? '' : ''}`} />
                    <div className={`rp-strength-bar ${metCount >= 3 ? passwordStrength ?? '' : ''}`} />
                    <div className={`rp-strength-bar ${metCount >= 5 ? passwordStrength ?? '' : ''}`} />
                  </div>
                  <span className={`rp-strength-txt ${passwordStrength ?? ''}`}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="rp-field">
              <label className="rp-field-label">{t('confirmPasswordLabel') || 'Confirm Password'}</label>
              <div className="rp-field-wrap">
                <span className="rp-field-icon"><Lock size={18} /></span>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  id="confirmPassword"
                  className={`rp-field-input ${passwordMatchError ? 'err' : passwordMatchOk ? 'ok' : ''}`}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder={t('confirmPasswordPlaceholder') || 'Repeat your password'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="rp-field-toggle"
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordMatchError && (
                <div className="rp-field-msg err-msg">
                  <XCircle size={13} />
                  {t('passwordsDoNotMatch') || 'Passwords do not match'}
                </div>
              )}
              {passwordMatchOk && (
                <div className="rp-field-msg ok-msg">
                  <CheckCircle2 size={13} />
                  Passwords match
                </div>
              )}
            </div>

            {/* Requirements */}
            <div className="rp-reqs">
              <div className="rp-reqs-title">{t('passwordRequirements') || 'Password Requirements'}</div>
              <ul className="rp-reqs-list">
                {[
                  { met: reqLen,     label: t('requirement1') || 'At least 8 characters' },
                  { met: reqUpper,   label: t('requirement2') || 'One uppercase letter' },
                  { met: reqLower,   label: t('requirement3') || 'One lowercase letter' },
                  { met: reqNum,     label: t('requirement4') || 'One number' },
                  { met: reqSpecial, label: t('requirement5') || 'One special character' },
                ].map((req, i) => (
                  <li key={i} className={`rp-req ${req.met ? 'met' : ''}`}>
                    <span className="rp-req-dot" />
                    {req.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="rp-btn-submit"
              disabled={isLoading}
            >
              {isLoading
                ? <><span className="rp-spinner" /> {t('processing') || 'Resetting…'}</>
                : (t('resetButton') || 'Reset Password')
              }
            </button>
          </form>

        </div>
      </main>

      {footer}
    </div>
  );
};

export default ResetPasswordPage;
